import apiClient from './apiClient';

/**
 * Service for managing the current user's account settings.
 * Covers username changes, password changes, and profile picture management.
 */
const accountService = {
  /**
   * Change the username of the currently authenticated user.
   * @param {string} newUsername - The desired new username (3–100 characters)
   * @returns {Promise<Object>} Response with new JWT token and updated user
   */
  changeUsername: (newUsername) =>
    apiClient.put('/users/me/username', { newUsername }),

  /**
   * Change the password of the currently authenticated user.
   * @param {string} currentPassword - The user's current password
   * @param {string} newPassword - The new password (min 8 characters)
   * @param {string} newPasswordConfirm - Confirmation of the new password
   * @returns {Promise<Object>} Response with updated UserDTO
   */
  changePassword: (currentPassword, newPassword, newPasswordConfirm) =>
    apiClient.put('/users/me/password', { currentPassword, newPassword, newPasswordConfirm }),

  /**
   * Upload a new profile picture for the currently authenticated user.
   * @param {File} file - JPEG or PNG image file (max 2 MB)
   * @returns {Promise<Object>} Response with updated UserDTO
   */
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/users/me/profile-picture', formData, {
      headers: { 'Content-Type': undefined }
    });
  },

  /**
   * Delete the profile picture of the currently authenticated user.
   * @returns {Promise<Object>} Response with updated UserDTO
   */
  deleteProfilePicture: () => apiClient.delete('/users/me/profile-picture'),

  /**
   * Build the URL for loading the current user's profile picture.
   * The URL must be fetched with an Authorization header (Bearer token).
   * @returns {string} Absolute URL to the profile picture endpoint
   */
  getProfilePictureUrl: () => `${apiClient.defaults.baseURL}/users/me/profile-picture`,

  /**
   * Delete the currently authenticated user's account permanently.
   *
   * Identity must be confirmed via exactly one of:
   *   - {@code currentPassword}: required for LOCAL, accepted for BOTH.
   *   - {@code reAuthCode + reAuthProvider}: fresh OAuth authorization code from the
   *     user's existing provider (Google/Microsoft/...). Required for OAUTH-only,
   *     accepted as an alternative for BOTH.
   *
   * {@code reAuthCodeVerifier} is optional and only relevant for the native
   * (Capacitor) DELETE_REAUTH flow (custom-scheme redirect + PKCE, see
   * utils/oauthStart.js): it is the PKCE code verifier matching the code
   * challenge sent when the re-auth redirect URL was requested, and must be
   * passed alongside {@code reAuthCode} in that case.
   *
   * @param {{ currentPassword?: string, reAuthCode?: string, reAuthProvider?: string, reAuthCodeVerifier?: string }} payload
   * @returns {Promise<void>} Resolves with undefined on success (204)
   */
  deleteAccount: (payload) =>
    apiClient.delete('/users/me', { data: payload }).then(() => undefined)
};

export default accountService;
